import { useMemo } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQueries } from "@tanstack/react-query"
import { type ColumnDef } from "@tanstack/react-table"
import { FileText } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { EntityTablePage } from "@/features/shared/components/entity-table-page"
import { useAnnotations } from "@/lib/hooks/use-annotations"
import { fetchInstrumentLite } from "@/lib/api/instruments"
import { useSites } from "@/lib/hooks/use-sites"
import type { Annotation } from "@/types/api"

export const Route = createFileRoute("/_authenticated/annotations")({
  component: AnnotationsPage,
})

const typeBadgeVariant: Record<string, "default" | "success" | "warning" | "destructive" | "secondary"> = {
  event: "default",
  risk: "destructive",
  schedule_activity: "warning",
  annotation: "secondary",
  general: "secondary",
  schedule_milestone: "success",
}

function getColumns(siteNameMap: Map<string, string>, instrumentNameMap: Map<string, string>): ColumnDef<Annotation, unknown>[] {
  return [
  {
    accessorKey: "site",
    header: "Site",
    cell: ({ row }) => siteNameMap.get(row.original.site) ?? row.original.site,
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => (
      <Badge variant={typeBadgeVariant[row.original.type] ?? "secondary"}>
        {row.original.type.replace(/_/g, " ")}
      </Badge>
    ),
  },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => (
      <span className="max-w-[400px] truncate block text-[var(--color-text-primary)]">
        {row.original.description}
      </span>
    ),
  },
  {
    accessorKey: "start_time",
    header: "Start Date",
    cell: ({ row }) => new Date(row.original.start_time).toLocaleDateString(),
  },
  {
    accessorKey: "end_time",
    header: "End Date",
    cell: ({ row }) =>
      row.original.end_time
        ? new Date(row.original.end_time).toLocaleDateString()
        : "—",
  },
  {
    accessorKey: "instrument",
    header: "Instrument",
    cell: ({ row }) => {
      const id = row.original.instrument
      if (!id) return "—"
      return instrumentNameMap.get(id) ?? id
    },
  },
  {
    accessorKey: "include_in_timeline",
    header: "Visible",
    cell: ({ row }) => (row.original.include_in_timeline ? "Yes" : "No"),
  },
]
}

function AnnotationsPage() {
  const { data: annotations, isLoading, isError, error } = useAnnotations()
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  const sitesWithInstruments = useMemo(() => {
    if (!annotations) return []
    const siteSet = new Set<string>()
    annotations.forEach((a) => { if (a.instrument) siteSet.add(a.site) })
    return Array.from(siteSet)
  }, [annotations])

  const instrumentQueries = useQueries({
    queries: sitesWithInstruments.map((site) => ({
      queryKey: ["instruments-lite", site],
      queryFn: () => fetchInstrumentLite(site),
    })),
  })

  const instrumentNameMap = useMemo(() => {
    const map = new Map<string, string>()
    instrumentQueries.forEach((q) => {
      q.data?.forEach((inst) => map.set(inst.id, inst.name))
    })
    return map
  }, [instrumentQueries])

  const columns = useMemo(() => getColumns(siteNameMap, instrumentNameMap), [siteNameMap, instrumentNameMap])

  return (
    <EntityTablePage
      icon={<FileText className="h-5 w-5 text-[var(--color-accent-blue)]" />}
      title="Annotations"
      description={`${annotations?.length ?? 0} annotations`}
      searchPlaceholder="Search annotations..."
      emptyMessage="No annotations"
      columns={columns}
      data={annotations}
      isLoading={isLoading}
      isError={isError}
      error={error}
    />
  )
}
